<script setup lang="ts">
    import { onMounted, inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Loading from '@shared/components/Loading.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import { UserAccess } from './User.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import { LanguagesAccess } from '@shared/Root/Languages.access';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import AddAccountToUser from './AddAccountToUser.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userAccess: UserAccess = injectorAccess.injector.get(UserAccess);
    const languagesAccess: LanguagesAccess = injectorAccess.injector.get(LanguagesAccess);

    const props = defineProps<{
        userId: string;
    }>();

    const state = reactive<{
        userForm: any;
        userFormReady: boolean;
        showPasswordModal: boolean;
        newPassword: string;
        showAddAccountModal: boolean;
        addingAccount: boolean;
    }>({
        userForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        userFormReady: false,
        showPasswordModal: false,
        newPassword: '',
        showAddAccountModal: false,
        addingAccount: false,
    });

    onMounted(async () => {
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();

            languagesAccess.getLanguages().then((languages) => {
                state.userForm.data.LanguageId.select.options = languages.map((language) => ({
                    value: language.LanguageId.toString(),
                    label: language.Name,
                }));
            });
        });
    });

    function setupUserFormData(): void {
        state.userForm.data = {
            FirstName: {
                modelValue: userAccess.state.user!.FirstName,
                group: 'User',
                required: false,
                input: {
                    label: 'First Name',
                    type: 'text',
                },
            },
            LastName: {
                modelValue: userAccess.state.user!.LastName,
                group: 'User',
                required: false,
                input: {
                    label: 'Last Name',
                    type: 'text',
                },
            },
            Email: {
                modelValue: userAccess.state.user!.Email,
                group: 'User',
                required: true,
                input: {
                    label: 'Email',
                    type: 'email',
                },
            },
            MobilePhone: {
                modelValue: userAccess.state.user!.MobilePhone,
                group: 'User',
                required: false,
                input: {
                    label: 'Mobile Phone',
                    type: 'text',
                },
            },
            Status: {
                modelValue: userAccess.state.user!.Status,
                group: 'User',
                required: true,
                select: {
                    label: 'Status',
                    options: [
                        { value: '0', label: 'Enabled' },
                        { value: '1', label: 'Disabled' },
                        { value: '2', label: 'Pending Activation' },
                    ],
                },
            },
            LanguageId: {
                modelValue: userAccess.state.user!.LanguageId,
                group: 'User',
                required: true,
                select: {
                    label: 'Language',
                    options: [],
                },
            },
            PersonNummer: {
                modelValue: userAccess.state.user!.Personnummer,
                group: 'User',
                required: false,
                input: {
                    label: 'Personnummer',
                    type: 'text',
                },
            },
            Created: {
                modelValue: userAccess.state.user!.Created,
                group: 'Info',
                required: false,
                input: {
                    label: 'Created',
                    type: 'date',
                    disabled: true,
                },
            },
            LastLogin: {
                modelValue: userAccess.state.user!.LastLogin,
                group: 'Info',
                required: false,
                input: {
                    label: 'Last Login',
                    type: 'date',
                    disabled: true,
                },
            },
            ConsecutiveFailedLogins: {
                modelValue: userAccess.state.user!.ConsecutiveFailedLogins,
                group: 'Info',
                required: false,
                input: {
                    label: 'Consecutive Failed Logins',
                    type: 'number',
                    disabled: true,
                },
            },
            LastAccountName: {
                modelValue: userAccess.state.user!.LastAccountName,
                group: 'Info',
                required: false,
                input: {
                    label: 'Account',
                    type: 'text',
                    disabled: true,
                },
            },
            Data: {
                modelValue: userAccess.state.user!.Data,
                group: 'Other',
                required: false,
                textArea: {
                    label: 'Data',
                },
            },
        };

        state.userFormReady = true;
    }

    async function saveUser() {
        if (!state.userFormReady) return;

        let user = userAccess.state.user!;
        const userData = state.userForm.data;

        user.FirstName = userData.FirstName.modelValue;
        user.LastName = userData.LastName.modelValue;
        user.Email = userData.Email.modelValue;
        user.MobilePhone = userData.MobilePhone.modelValue;
        user.Status = userData.Status.modelValue;
        user.LanguageId = userData.LanguageId.modelValue;
        user.Personnummer = userData.PersonNummer.modelValue;
        user.Data = userData.Data.modelValue;

        await userAccess.saveUser(user);
    }

    function openPasswordModal() {
        state.showPasswordModal = true;
    }

    async function setPassword() {
        if (state.newPassword) {
            await userAccess.setPassword(props.userId, state.newPassword);
            state.showPasswordModal = false;
            state.newPassword = '';
        }
    }

    async function removeAccount(accountId: string) {
        await userAccess.removeAccount(props.userId, accountId);
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }

    async function setDefaultAccount(accountId: string) {
        await userAccess.setDefaultAccount(props.userId, accountId);
        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }

    function openAddAccountModal() {
        state.showAddAccountModal = true;
        focusAddAccountSearchInput();
    }

    function focusAddAccountSearchInput(): void {
        setTimeout(() => {
            document.getElementById('add-account-list-search')?.focus();
        }, 50);
    }

    async function addAccount(accountId: string) {
        state.addingAccount = true;
        await userAccess.addUserToAccount(props.userId, accountId).finally(() => {
            state.addingAccount = false;
            state.showAddAccountModal = false;
        });

        await userAccess.getUser(props.userId).then(() => {
            setupUserFormData();
        });
    }
</script>

<template>
    <p class="fw-600 m-lg-t m-xs-b">
        <router-link :to="{ name: 'UserList' }">
            <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
            Users
        </router-link>
    </p>

    <Loading :show="userAccess.state.loadingUser" loadingStyle="progress-bar"></Loading>

    <div v-if="userAccess.state.user">
        <div v-if="!userAccess.state.loadingUser">
            <h3>
                <i class="fa fa-square" :class="userAccess.getStatusClass(userAccess.state.user.StatusName)" aria-hidden="true"></i>
                {{ userAccess.state.user.FirstName }} {{ userAccess.state.user.LastName }}
                <small class="m-xs-l">
                    <router-link
                        v-if="userAccess.state.user.DefaultAccountId"
                        :to="{ name: 'AccountEdit', params: { accountId: userAccess.state.user.DefaultAccountId } }">
                        {{ userAccess.state.user.LastAccountName }}
                    </router-link>
                </small>
            </h3>
            <p>{{ userAccess.state.user.Email }}</p>

            <CopyButton buttonText="Copy user id" :textToCopy="userAccess.state.user.UserId" :tooltip="userAccess.state.user.UserId" />

            <button @click="openPasswordModal" class="btn btn-default btn-sm m-xs-l" :disabled="userAccess.state.settingPassword">Set Password</button>

            <tabs navClass="tabs list-unstyled">
                <tab title="User">
                    <Form
                        v-if="state.userFormReady"
                        class="fieldset-grid"
                        :data="state.userForm.data"
                        :submitting="userAccess.state.savingUser"
                        @update:isValid="(key, newVal) => (state.userForm!.data[key].isValid = newVal)"
                        @update:modelValue="(key, newVal) => (state.userForm.data[key].modelValue = newVal)"
                        @onSubmit="saveUser"></Form>
                </tab>
                <tab title="Accounts">
                    <List
                        displayAs="table"
                        itemId="AccountId"
                        :hideSearch="true"
                        :items="userAccess.state.user.Accounts"
                        :tableHeaders="['#', 'Account', 'Client Company', 'Role', '']">
                        <template #toolbar>
                            <button class="btn btn-primary" @click="openAddAccountModal">Add to account</button>
                        </template>
                        <template #item="{ item: account, index: index }">
                            <td>{{ index + 1 }}.</td>
                            <td>
                                <router-link :to="{ name: 'AccountEdit', params: { accountId: account.AccountId } }">
                                    {{ account.Name }}
                                    <span v-if="account.AccountId == userAccess.state.user.DefaultAccountId">(Default)</span>
                                </router-link>
                            </td>
                            <td>
                                {{ account.ClientCompany.Name }}
                            </td>
                            <td>{{ account.UserAccountRoleName }}</td>
                            <td class="autofit">
                                <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="true">
                                    <template #toggle>
                                        ...
                                        <span class="caret m-xs-l"></span>
                                    </template>
                                    <template #content>
                                        <li>
                                            <a @click="removeAccount(account.AccountId)">Remove</a>
                                        </li>
                                        <li v-if="account.AccountId !== userAccess.state.user.DefaultAccountId">
                                            <a @click="setDefaultAccount(account.AccountId)">Set default</a>
                                        </li>
                                    </template>
                                </BSDropdownButton>
                            </td>
                        </template>
                    </List>
                </tab>
            </tabs>
        </div>
    </div>

    <Modal :show="state.showPasswordModal" @close="() => (state.showPasswordModal = false)" title="Set Password" :close-on-click-outside="true">
        <template #content>
            <Loading :show="userAccess.state.settingPassword" loadingStyle="progress-bar"></Loading>
            <div class="form-group" v-if="!userAccess.state.settingPassword">
                <label for="newPassword">New Password</label>
                <input type="password" id="newPassword" v-model="state.newPassword" class="form-control" />
            </div>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" @click="setPassword" :disabled="userAccess.state.settingPassword">Save</button>
        </template>
    </Modal>

    <Modal
        v-if="userAccess.state.user"
        :show="state.showAddAccountModal"
        @close="() => (state.showAddAccountModal = false)"
        title="Add user to account"
        :close-on-click-outside="true"
        size="lg">
        <template #content>
            <AddAccountToUser :user-accounts="userAccess.state.user.Accounts" @add-account="addAccount" />
        </template>
    </Modal>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
